import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useAudienceStore } from '@/stores/audience-store';
import { WIZARD_STEPS, buildStepPath } from '@/components/shared/wizard-tabs/steps';
import { useAutosave, type SaveOptions } from './use-autosave';
import { useLocalDraftMirror } from './use-local-draft-mirror';
import { useFlushTriggers } from './use-flush-triggers';
import { useDraftBoot } from './use-draft-boot';
import { useDraftStoreSubscriber } from './use-draft-store-subscriber';
import { useDraftActions } from './use-draft-actions';
import { createDraft, patchDraft } from '../services/audience-draft-api';
import type { DraftPatch } from '../types/draft';

export interface UseAudienceDraftArgs {
  idFromUrl: string | undefined;
  /** NPJB-358: Campaign ID from ?campaignId query param. When this changes,
   * all draft state must reset even if idFromUrl stays undefined. */
  campaignIdFromUrl?: string;
}

export interface UseAudienceDraftReturn {
  autosaveState: ReturnType<typeof useAutosave>['state'];
  isSaveInFlight: boolean;
  showRecoveryBanner: boolean;
  isBooting: boolean;
  applyLocalRecovery: () => void;
  discardLocalRecovery: () => void;
  reloadLatestFromServer: () => Promise<void>;
  keepMyChanges: () => Promise<void>;
  proceedToReview: () => Promise<void>;
  submitNow: () => Promise<void>;
  flushBeforeSubmit: () => Promise<void>;
  discardPendingSave: () => void;
}

/**
 * Audience-builder draft orchestration.
 *
 * Identity model:
 *   - The URL is the single source of truth for which draft this session edits.
 *   - The local mirror is keyed by `idFromUrl` (or "new" before a draft exists).
 *   - When `idFromUrl` changes, every internal ref is reset and the store is
 *     reset. No state leaks across drafts.
 *
 * Recovery model:
 *   - Prompt is shown ONLY when the local mirror's content differs from the
 *     server's content (deep equal) AND local is newer than server.
 *   - Applying recovery explicitly marks the autosave dirty so a real save
 *     actually fires.
 */
export function useAudienceDraft({
  idFromUrl,
  campaignIdFromUrl,
}: UseAudienceDraftArgs): UseAudienceDraftReturn {
  // Mirror is keyed strictly by the URL identity. Never fall back to
  // storeDraftId — it could leak one draft's mirror into another's session.
  const mirror = useLocalDraftMirror(idFromUrl ?? null);
  const navigate = useNavigate();

  const boot = useDraftBoot(idFromUrl, mirror, campaignIdFromUrl);
  const { setLastSavedPatch, isHydrating, getLastSavedPatch, hydrate } = boot;

  // True while the first save creates the draft. A page-exit save sent then must not
  // create a second one; it fails, and its edits stay queued and in the local mirror.
  const creatingRef = useRef(false);

  const save = useCallback(
    async (patch: DraftPatch, options?: SaveOptions): Promise<unknown> => {
      const { draftId, lastSeenUpdatedAt, campaignId } = useAudienceStore.getState();
      if (!draftId) {
        if (creatingRef.current) throw new Error('The draft is still being created');
        creatingRef.current = true;
        let created: Awaited<ReturnType<typeof createDraft>>;
        try {
          created = await createDraft(patch, campaignId ?? undefined);
        } finally {
          creatingRef.current = false;
        }
        setLastSavedPatch(patch);
        mirror.clear();
        useAudienceStore.getState().setDraftIdentity({
          draftId: created.id,
          lastSeenUpdatedAt: created.updatedAt,
          status: created.status,
          following: created.following,
        });
        // Reflect the new draft id into the URL THROUGH the router. window.history
        // .replaceState bypasses the data router (createBrowserRouter), which leaves
        // location/useParams stale and can revert the address bar on the next render —
        // so use navigate(replace). Same step, replace so Back skips the id-less URL.
        // useDraftBoot sees the new id === store draftId and skips a re-boot/refetch.
        const currentPath = globalThis.location.pathname;
        const currentStep = WIZARD_STEPS.find(
          (s) => currentPath === s.path || currentPath.startsWith(`${s.path}/`),
        );
        if (currentStep) {
          navigate(buildStepPath(currentStep.path, created.id), { replace: true });
        }
        return created;
      }
      const updated = await patchDraft(draftId, patch, lastSeenUpdatedAt, options);
      setLastSavedPatch(patch);
      // B15: the PATCH response's updatedAt is the next save's concurrency token. Older
      // responses lack it; then clear the token so the next PATCH skips the check
      // instead of failing against our own save.
      useAudienceStore.setState({ lastSeenUpdatedAt: updated.updatedAt ?? null });
      mirror.clear();
      return updated;
    },
    [mirror, setLastSavedPatch, navigate],
  );

  const autosave = useAutosave({ save });

  useDraftStoreSubscriber({
    mirror,
    markDirty: autosave.markDirty,
    isHydrating,
    getLastSavedPatch,
  });

  useFlushTriggers({ flushNow: autosave.flushNow, enabled: true });

  const actions = useDraftActions({
    autosave,
    mirror,
    getPendingLocal: boot.getPendingLocal,
    setPendingLocal: boot.setPendingLocal,
    setShowRecoveryBanner: boot.setShowRecoveryBanner,
    hydrate,
    setLastSavedPatch,
  });

  return {
    autosaveState: autosave.state,
    isSaveInFlight: autosave.isSaveInFlight,
    showRecoveryBanner: boot.showRecoveryBanner,
    isBooting: boot.isBooting,
    discardPendingSave: autosave.discardPending,
    ...actions,
  };
}
